import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Pagination, Result, Select, Spin, Table, Tooltip, Typography } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import agentQueryLogService, { IAgentQueryLog } from '../../../services/aiChat/agentQueryLog.service';
import agentTokenService, { IAgentToken } from '../../../services/aiChat/agentToken.service';
import './agentQueryLog.less';

const { Text } = Typography;
const { Option } = Select;

const PAGE_SIZE = 50;

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDuration = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;

const AgentQueryLog: React.FC = () => {
  const [logs, setLogs] = useState<IAgentQueryLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const [tokens, setTokens] = useState<IAgentToken[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<number | undefined>(undefined);

  const [sqlModal, setSqlModal] = useState<{ sql: string; description: string } | null>(null);

  // Keep a ref to avoid stale closure in manual refresh
  const selectedTokenIdRef = useRef(selectedTokenId);
  selectedTokenIdRef.current = selectedTokenId;
  const pageRef = useRef(page);
  pageRef.current = page;

  const loadLogs = useCallback(async (currentPage: number, tokenId?: number) => {
    setLoading(true);
    try {
      const result = await agentQueryLogService.list(currentPage, PAGE_SIZE, tokenId);
      setLogs(result.data ?? []);
      setTotal(result.count ?? 0);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTokens = useCallback(async () => {
    try {
      const data = await agentTokenService.list();
      setTokens(Array.isArray(data) ? data : []);
    } catch {
      // Non-critical — filter just won't be populated
    }
  }, []);

  useEffect(() => {
    loadTokens();
    loadLogs(1, undefined);
  }, [loadLogs, loadTokens]);

  const handleFilterChange = (value: number | undefined) => {
    setSelectedTokenId(value);
    setPage(1);
    loadLogs(1, value);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadLogs(newPage, selectedTokenIdRef.current);
  };

  const handleRefresh = () => {
    loadLogs(pageRef.current, selectedTokenIdRef.current);
  };

  const emptyText =
    selectedTokenId != null
      ? 'No queries found for this agent.'
      : 'No agent queries have been logged yet.';

  const columns: ColumnsType<IAgentQueryLog> = [
    {
      title: 'Executed At',
      key: 'executed_at',
      width: 180,
      render: (_, r) => formatDateTime(r.executed_at),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Agent',
      dataIndex: 'token_label',
      key: 'token_label',
      render: (val: string) => <Text strong>{val}</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Rows',
      key: 'row_count',
      width: 90,
      align: 'right',
      render: (_, r) => (
        <span>
          {r.row_count.toLocaleString()}
          {r.capped && (
            <Tooltip title="Result was capped at 500 rows">
              <WarningOutlined className="query-log-capped-icon" />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Duration',
      key: 'duration_ms',
      width: 100,
      align: 'right',
      render: (_, r) => <Text type="secondary">{formatDuration(r.duration_ms)}</Text>,
    },
    {
      title: 'SQL',
      key: 'sql',
      render: (_, r) => (
        <span className="query-log-sql-preview">
          <Text code className="query-log-sql-text">
            {r.sql.length > 80 ? `${r.sql.slice(0, 80)}…` : r.sql}
          </Text>
          <Button
            type="link"
            size="small"
            onClick={() => setSqlModal({ sql: r.sql, description: r.description })}
          >
            View
          </Button>
        </span>
      ),
    },
  ];

  if (accessDenied) {
    return (
      <div className="query-log-access-denied">
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have access to this page. Global Admin role is required."
        />
      </div>
    );
  }

  return (
    <div className="query-log-page">
      <div className="query-log-header">
        <Typography.Title level={4} className="query-log-title">
          Agent Query Log
        </Typography.Title>

        <div className="query-log-filters">
          <Select
            style={{ width: 220 }}
            placeholder="All Agents"
            allowClear
            value={selectedTokenId}
            onChange={handleFilterChange}
            loading={tokens.length === 0 && loading}
          >
            {tokens.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.label}
              </Option>
            ))}
          </Select>

          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        <Table<IAgentQueryLog>
          rowKey="id"
          columns={columns}
          dataSource={logs}
          pagination={false}
          locale={{ emptyText }}
          className="query-log-table"
        />
      </Spin>

      {total > PAGE_SIZE && (
        <div className="query-log-pagination">
          <Text type="secondary">{total.toLocaleString()} total records</Text>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
          />
        </div>
      )}

      <Modal
        wrapClassName="custom-modal"
        title={sqlModal?.description || 'SQL Query'}
        centered
        visible={!!sqlModal}
        onCancel={() => setSqlModal(null)}
        footer={<Button onClick={() => setSqlModal(null)}>Close</Button>}
        width={700}
      >
        {sqlModal && (
          <pre className="query-log-sql-full">{sqlModal.sql}</pre>
        )}
      </Modal>
    </div>
  );
};

export default AgentQueryLog;
